import React, { useState } from 'react';
import API_BASE_URL from '../config';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { useNavigate } from 'react-router-dom';
import KvkkModal from '../components/KvkkModal';

import Layout from '../components/Layout';
import Info from '../components/Info';
import Main from '../components/Main';
import Quiz from '../components/Quiz';
import Result from '../components/Result';
import TieBreaker from '../components/TieBreaker';
import Loader from '../components/Loader';
import { shuffle } from '../utils';

const TestPage = () => {
  const [showKvkkModal, setShowKvkkModal] = useState(true);
  const [kvkkAccepted, setKvkkAccepted] = useState(false);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(null);
  const [data, setData] = useState(null);
  const [mainQuestions, setMainQuestions] = useState([]);
  const [tieBreakers, setTieBreakers] = useState([]);
  const [isQuizStarted, setIsQuizStarted] = useState(false);
  const [isQuizCompleted, setIsQuizCompleted] = useState(false);
  const [isTieBreakerPhase, setIsTieBreakerPhase] = useState(false);
  const [isInfoStep, setIsInfoStep] = useState(true);
  const [resultData, setResultData] = useState(null);
  const [userInfo, setUserInfo] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [tempResultData, setTempResultData] = useState(null);

  const proceedToForm = () => setIsInfoStep(false);

  const startQuiz = (quizData, userDetails) => {
    setLoading(true);
    setLoadingMessage({
      title: 'Test hazırlanıyor...',
      message: 'Lütfen bekleyin',
    });

    const parsed = quizData.map((group, i) => {
      const allOptions = group.rawOptions || group.options || [];
      const mainOpts = allOptions.filter(opt => !opt.is_tiebreaker);
      const tieOpts = allOptions.filter(opt => opt.is_tiebreaker);

      return {
        ...group,
        order: parseInt(group.order ?? i),
        rawOptions: mainOpts,
        tieOptions: tieOpts,
      };
    });

    const allTieOptions = parsed.flatMap(q => q.tieOptions || []).filter(opt => opt.is_tiebreaker);

    const main = parsed
      .filter(q => q.rawOptions?.length > 0 && q.order < 10)
      .map(q => ({
        ...q,
        options: shuffle(q.rawOptions),
      }));

    const tie = [10].map((order, index) => ({
      question: 'Sizi EN İYİ TANIMLAYAN ifadeyi seçin',
      options: shuffle(allTieOptions),
      order,
    }));

    setTimeout(() => {
      setData(parsed);
      setMainQuestions(main);
      setTieBreakers(tie);
      setUserInfo(userDetails);
      setIsQuizStarted(true);
      setLoading(false);
    }, 1000);
  };

  const endQuiz = async (result) => {
    setLoading(true);
    setLoadingMessage({
      title: 'Cevaplar kaydediliyor...',
      message: 'Lütfen bekleyin',
    });

    const sid = uuidv4();
    setSessionId(sid);

    try {
      await axios.post(`${API_BASE_URL}/api/session/`, {
        session_id: sid,
        full_name: userInfo.full_name,
        email: userInfo.email,
        phone_number: userInfo.phone_number,
        company: userInfo.company,
        role: userInfo.role,
        token: userInfo.token,  // ← Token'ı da ekliyoruz
      });

      const answers = result.questionsAndAnswers.flatMap(q =>
        q.selectedOptionIds.map(optionId => ({ option_id: optionId }))
      );

      await axios.post(`${API_BASE_URL}/api/response/`, {
        session_id: sid,
        answers,
      });

      setTempResultData(result);
      setIsQuizStarted(false);
      setIsTieBreakerPhase(true);
      setLoading(false);
    } catch (err) {
      console.error("Cevap gönderme hatası:", err);
      setLoading(false);
    }
  };

  const handleTieBreakerComplete = async () => {
    setLoading(true);
    setLoadingMessage({
      title: 'Sonuç hesaplanıyor...',
      message: 'Kişilik tipin belirleniyor',
    });

    try {
      const res = await axios.get(`${API_BASE_URL}/api/result/${sessionId}/`);
      const resultType = res.data.result;
      const resultName = res.data.result_name;

      await axios.post(`${API_BASE_URL}/api/send-email/`, {
        email: userInfo.email,
        full_name: userInfo.full_name,
        phone_number: userInfo.phone_number,
        company: userInfo.company,
        role: userInfo.role,
        result_type: resultType,
        result_name: resultName,
        token: userInfo.token,  // ← Token burada da önemli
      });

      setResultData({
        ...tempResultData ?? {},
        resultType,
        resultName,
      });

      setIsTieBreakerPhase(false);
      setIsQuizCompleted(true);
      setLoading(false);
    } catch (err) {
      console.error("Sonuç çekme/mail hatası:", err);
      setLoading(false);
    }
  };

  const resetQuiz = () => {
    setLoading(true);
    setLoadingMessage({ title: 'Anasayfaya dönülüyor...', message: 'Teşekkür ederiz!' });

    setTimeout(() => {
      setData(null);
      setMainQuestions([]);
      setTieBreakers([]);
      setIsQuizStarted(false);
      setIsQuizCompleted(false);
      setIsTieBreakerPhase(false);
      setResultData(null);
      setUserInfo(null);
      setTempResultData(null);
      setSessionId(null);
      setIsInfoStep(true);
      setLoading(false);
    }, 1000);
  };

  return (
    <Layout>
      {loading && <Loader {...loadingMessage} />}

      {!loading && isInfoStep && <Info onNext={proceedToForm} />}

      {!loading && !isInfoStep && !isQuizStarted && !isQuizCompleted && !isTieBreakerPhase && (
        <Main startQuiz={startQuiz} />
      )}

      {!loading && isQuizStarted && mainQuestions.length > 0 && (
        <Quiz data={mainQuestions} endQuiz={endQuiz} userInfo={userInfo} />
      )}

      {!loading && isTieBreakerPhase && tieBreakers.length > 0 && sessionId && (
        <TieBreaker
          questions={tieBreakers}
          sessionId={sessionId}
          onComplete={handleTieBreakerComplete}
          userInfo={userInfo}
          tempResultData={tempResultData}
        />
      )}

      {!loading && isQuizCompleted && resultData && (
        <Result
          questionsAndAnswers={resultData.questionsAndAnswers}
          replayQuiz={resetQuiz}
          resetQuiz={resetQuiz}
          totalQuestions={resultData.totalQuestions}
          resultType={resultData.resultType}
          resultName={resultData.resultName}
          userInfo={userInfo}
        />
      )}

      <KvkkModal
        open={showKvkkModal}
        onAccept={() => {
          setKvkkAccepted(true);
          setShowKvkkModal(false);
        }}
        onReject={() => {
          navigate('/');
        }}
      />
    </Layout>
  );
};

export default TestPage;
